import React, {
  useEffect,
  useRef,
  useCallback,
  memo,
  forwardRef,
  useImperativeHandle,
} from 'react'
import {
  object,
  func,
  string,
  bool,
} from 'prop-types'
import isEqual from 'lodash/isEqual'

const ControlledAddressWidget = forwardRef(({
  value,
  onChange,
  onReady,
  apihost,
  addressWidgetToken: token,
  fieldsConfig,
  layoutConfig,
  locale,
  spaEntry,
  disablePrediction,
  required,
}, ref) => {
  const containerRef = useRef(null)
  const widgetRef = useRef(null)
  const methodsRef = useRef(null)
  const lastValueRef = useRef(value)

  const handleValidate = useCallback(async ({ shouldTouchFields }) => {
    if (methodsRef.current && methodsRef.current.validate) {
      const res = await methodsRef.current.validate({ shouldTouchFields })
      return res.isValid
    }
    return true
  }, [])

  useImperativeHandle(ref, () => ({
    validate: ({ shouldTouchFields }) => handleValidate({ shouldTouchFields }),
  }), [handleValidate])

  useEffect(() => {
    if (methodsRef.current && !isEqual(value, lastValueRef.current)) {
      methodsRef.current.setValue(value || {})
      lastValueRef.current = value
    }
  }, [value])

  useEffect(() => {
    if (token && methodsRef.current) {
      methodsRef.current.updateToken(token)
    }
  }, [token])

  const handleWidgetChange = useCallback(newValue => {
    if (!isEqual(newValue, lastValueRef.current)) {
      lastValueRef.current = newValue
      onChange?.(newValue)
    }
  }, [onChange])

  useEffect(() => {
    if (!containerRef.current || widgetRef.current || !spaEntry || !apihost) return null

    const widget = document.createElement('active-widget')

    widget.setAttribute('spa-entry', spaEntry)
    widget.setAttribute('spa-scope', 'activeSpaWidgets')
    widget.setAttribute('spa-module', './Address')

    widget.config = {
      apiHost: apihost,
      token,
      required,
      value,
      fieldsConfig,
      locale,
      disablePrediction,
      layoutConfig,
      onChange: handleWidgetChange,
      onReady,
      registerEvents: methods => {
        methodsRef.current = methods
      },
    }

    containerRef.current.appendChild(widget)
    widgetRef.current = widget

    return () => {
      if (widgetRef.current?.parentNode) {
        widgetRef.current.parentNode.removeChild(widgetRef.current)
        widgetRef.current = null
        methodsRef.current = null
      }
    }
  }, [
    apihost,
    fieldsConfig,
    locale,
    spaEntry,
    disablePrediction,
    onReady,
    handleWidgetChange,
  ])

  return (
    <div
      ref={containerRef}
      className="controlled-address-widget"
    />
  )
})

ControlledAddressWidget.propTypes = {
  value: object,
  onChange: func,
  apihost: string.isRequired,
  token: string.isRequired,
  fieldsConfig: object,
  layoutConfig: object,
  locale: string,
  spaEntry: string.isRequired,
  required: bool,
}

ControlledAddressWidget.defaultProps = {
  required: false,
}

export default memo(ControlledAddressWidget)
